import { Component, Input, computed, signal } from '@angular/core';
import { CashRequestLine } from '../../../models/cash-request/cash-request';
import { ResponseTranslation } from '../../../models/Translations';

interface BreakdownRow {
  /** The account number — the stable key rows are grouped and tracked by. */
  concept: string;
  /** Localized account name, when the line carries one; the template falls back to the number. */
  name?: ResponseTranslation | null;
  amount: number;
}

/**
 * Reusable Cash Request breakdown — groups a request's lines by line type into CONCEPT / AMOUNT rows
 * with a total. Shown on the Cash Request details (the "Request total · by line type" panel) and on the
 * Cash Funding details (the "Cash request breakdown" card).
 */
@Component({
  selector: 'app-cash-request-breakdown',
  templateUrl: './cash-request-breakdown.component.html',
  styleUrl: './cash-request-breakdown.component.scss',
})
export class CashRequestBreakdownComponent {
  private readonly _lines = signal<CashRequestLine[]>([]);

  @Input() set lines(value: CashRequestLine[] | null | undefined) {
    this._lines.set(value ?? []);
  }

  /** Label shown next to amounts. Line totals are USD (#4863) — every entry contributes its functional
   *  amount — so callers should pass 'USD' unless they are showing something else. */
  @Input() currencyLabel = 'USD';

  /** Optional label for the total row (defaults to "Total"). */
  @Input() totalLabel = '';

  readonly rows = computed<BreakdownRow[]>(() => {
    // A line IS a GL account (#4863), so grouping by account number is a formality that also protects the
    // panel from legacy data where two lines shared one account.
    const grouped = new Map<string, BreakdownRow>();
    for (const line of this._lines()) {
      const concept = line.glAccount?.number || '—';
      const row = grouped.get(concept);
      if (row) row.amount += line.amount ?? 0;
      else grouped.set(concept, { concept, name: line.accountName, amount: line.amount ?? 0 });
    }
    return Array.from(grouped.values());
  });

  readonly total = computed(() => this.rows().reduce((sum, r) => sum + r.amount, 0));
}
